import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { setAppVersion } from 'redux/store/AppReducer';
import styled from 'styled-components';
import { OfferTransferComplex } from 'types/offerTransfer';
import { Message, sendReactNativeMessage } from 'utils/ReactNativeMessage';

function LikeComplexMap() {
  const map = useRef<naver.maps.Map>();
  const dispatch = useDispatch();
  const [likeListData, setLikeListData] = useState<OfferTransferComplex[]>([]);
  const markersRef = useRef<naver.maps.Marker[]>([]); // 마커를 저장할 배열

  const initWebViewMessageListener = async () => {
    if (!isMobile) {
      return;
    }

    if (!window.ReactNativeWebView) {
      return;
    }

    const webviewListener = (event: any) => {
      try {
        const { payload, type } = JSON.parse(event.data);

        switch (type) {
          case 'setData':
            setLikeListData(payload.data);
            break;
          case 'setAppVersion':
            dispatch(setAppVersion(payload.appVersion));
            break;

          default:
            break;
        }
      } catch (error) {
        console.log('json parse error', error);
      }
    };

    document.addEventListener('message', webviewListener);
    window.addEventListener('message', webviewListener);
  };

  const handleMarkerClick = (complexId: string, areaInPyeong: string) => {
    const message: Message = {
      type: 'openComplexInfo',
      payload: {
        complexId,
        areaInPyeong,
      },
    };
    sendReactNativeMessage(message);
  };

  useLayoutEffect(() => {
    drawMap();
    initWebViewMessageListener();
  }, []); // 초기 맵 설정

  useEffect(() => {
    // likeListData가 업데이트될 때마다 마커 그리기
    if (likeListData.length > 0) {
      // 기존 마커 제거
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = []; // 마커 배열 초기화

      likeListData.forEach((data) => {
        drawCustomMarker(
          data?.latitude,
          data?.longitude,
          data?.complexName,
          data?.complexId.toString(),
          data?.areaInPyeong.toString(),
        );
      });

      // 첫 번째 좌표로 맵의 중심을 이동
      const bounds = new naver.maps.LatLngBounds(
        new naver.maps.LatLng(
          likeListData[0].latitude,
          likeListData[0].longitude,
        ),
        new naver.maps.LatLng(
          likeListData[0].latitude,
          likeListData[0].longitude,
        ),
      );

      likeListData.forEach((data) => {
        bounds.extend(new naver.maps.LatLng(data.latitude, data.longitude));
      });

      // 여백 추가 (예: 0.01도만큼)
      const northEast = bounds.getNE();
      const southWest = bounds.getSW();

      const extendedBounds = new naver.maps.LatLngBounds(
        new naver.maps.LatLng(northEast.lat() + 0.01, northEast.lng() + 0.01), // 북동쪽에 여백 추가
        new naver.maps.LatLng(southWest.lat() - 0.01, southWest.lng() - 0.01), // 남서쪽에 여백 추가
      );

      // bounds를 기준으로 맵의 중심과 줌 레벨 조정
      map.current?.fitBounds(extendedBounds);
    }
  }, [likeListData]);

  // 커스텀 마커를 추가하는 함수 (텍스트 포함)
  const drawCustomMarker = (
    latitude: number,
    longitude: number,
    title: string,
    complexId: string,
    areaInPyeong: string,
  ) => {
    if (map.current) {
      const markerPosition = new naver.maps.LatLng(latitude, longitude);
      // 커스텀 마커 생성 (HTML로 생성)
      const defaultIcon = [
        `<div style="position: absolute; top: 0; left: 0; display:flex; flex-direction: column; align-items: center; z-index:0;" >`,
        '<div style=" height: 40px; width:100px; background-color: #4584FF;  display:flex; align-items: center; justify-content: center; flex-direction: column; box-shadow: 0px 0px 8px rgba(0, 30, 81, 0.12); border-radius: 8px; border: 1px solid #3C76E7;">',
        `   <div style="font-size:12px;color:#FFFFFF; font-weight:bold;">${
          title?.length > 6 ? `${title.slice(0, 6)}...` : title
        }</div>`,
        `   <div style="font-size:8px;color:#FFFFFF; margin-top: 2px; ">${areaInPyeong}평</div>`,
        '</div>',
        '<div style="width:0px; height:0px; border-left: 12px solid transparent; border-right: 12px solid transparent; border-top: 12px solid #3C76E7; position: relative;"></div>',
        '<div style= "width: 0;height: 0; border-left: 12px solid transparent; border-right: 12px solid transparent; border-top: 12px solid #4584FF ; position: absolute; bottom: 2px; left: 39px;"></div>',
        '</div>',
      ].join('');

      // 커스텀 마커 옵션 설정
      const marker = new naver.maps.Marker({
        position: markerPosition,
        map: map.current,
        icon: {
          content: defaultIcon, // 커스텀 HTML 사용
          anchor: new naver.maps.Point(30, 30), // 앵커 포인트를 하단 중앙으로 조정
        },
        title,
      });

      // 마커 클릭 이벤트 추가
      naver.maps.Event.addListener(marker, 'click', () => {
        handleMarkerClick(complexId, areaInPyeong); // 마커 클릭 시 complexId로 handleMarkerClick 호출
      });

      // 마커를 배열에 추가
      markersRef.current.push(marker);
    }
  };

  async function drawMap() {
    const mapOptions: naver.maps.MapOptions = {
      zoom: 15,
      tileSpare: 20,
    };

    map.current = new naver.maps.Map('map', mapOptions);
  }

  return (
    <>
      <NaverMap id="map"></NaverMap>
    </>
  );
}

export default LikeComplexMap;

const NaverMap = styled.div`
  width: 100%;
  height: 100%;
`;
